import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API, apiErr, runJob } from "@/lib/api";
import { ui, statusStyle } from "@/lib/ui";
import {
  Play, RotateCcw, CheckCircle2, XCircle, Loader2, Circle, Timer,
  ExternalLink, Monitor, Smartphone, History, ArrowLeft, RefreshCw, Copy,
  Wand2, Check, Rocket, Globe, MessageSquare, Send, Download, Pencil, Paperclip, X, Eye, Undo2, Redo2,
} from "lucide-react";

const fmtTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = (secs % 60).toFixed(1).padStart(4, "0");
  return `${String(m).padStart(2, "0")}:${s}`;
};

function StepRow({ step }) {
  const icon = {
    completed: <CheckCircle2 className="w-5 h-5 text-emerald-400" />,
    in_progress: <Loader2 className="w-5 h-5 text-indigo-400 spin" />,
    failed: <XCircle className="w-5 h-5 text-rose-400" />,
    pending: <Circle className="w-5 h-5 text-slate-600" />,
  }[step.status];
  const cls = {
    completed: "bg-emerald-950/20 border-emerald-500/30 text-emerald-200",
    in_progress: "bg-indigo-950/20 border-indigo-500/40 text-indigo-200",
    failed: "bg-rose-950/20 border-rose-500/30 text-rose-200",
    pending: "bg-slate-950/50 border-slate-800/60 text-slate-500",
  }[step.status];
  return (
    <div className={`flex items-center gap-3 p-3.5 rounded-lg border transition-colors ${cls}`} data-testid={`step-${step.key}`}>
      {icon}
      <span className="text-sm font-medium flex-1">{step.label}</span>
      {step.status === "failed" && step.error && <span className="text-xs text-rose-300 max-w-xs truncate">{step.error}</span>}
    </div>
  );
}

export default function SiteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [job, setJob] = useState(null);
  const [versions, setVersions] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [device, setDevice] = useState("desktop");
  const [logo, setLogo] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [includeWww, setIncludeWww] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [domainBusy, setDomainBusy] = useState(false);
  const [domainStatus, setDomainStatus] = useState({});
  const [showDomainInput, setShowDomainInput] = useState(false);
  const [gscBusy, setGscBusy] = useState(false);
  const [gscMsg, setGscMsg] = useState("");
  const [editInstruction, setEditInstruction] = useState("");
  const [editRef, setEditRef] = useState([]);
  const [editBusy, setEditBusy] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [approving, setApproving] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [form, setForm] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);

  const startEdit = () => {
    setForm({
      name: site.name || "",
      phone: site.business?.phone || "",
      address: site.business?.address || "",
      city: site.business?.city || "",
      state: site.business?.state || "",
      zip: site.business?.zip || "",
      hours: site.business?.hours || "",
      hours_247: !!site.business?.hours_247,
    });
    setEditingDetails(true);
  };

  const saveDetails = async () => {
    setSavingDetails(true);
    try {
      const business = {
        ...site.business,
        name: form.name, phone: form.phone, address: form.address,
        city: form.city, state: form.state, zip: form.zip,
        hours: form.hours, hours_247: form.hours_247,
      };
      const { data } = await api.put(`/sites/${id}`, { name: form.name, business });
      setSite(data);
      setEditingDetails(false);
      toast.success("Business details updated");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingDetails(false); }
  };

  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const iframeRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/sites/${id}`);
    if (data.site_type === "national") { navigate(`/national/${id}`, { replace: true }); return; }
    setSite(data);
    const v = await api.get(`/sites/${id}/versions`);
    setVersions(v.data);
  }, [id]);

  const fetchStatus = useCallback(async () => {
    const { data } = await api.get(`/sites/${id}/generation-status`);
    if (data.status === "none") { setJob(null); return; }
    setJob(data);
    if (data.status !== "running") {
      clearInterval(pollRef.current); pollRef.current = null;
      clearInterval(timerRef.current); timerRef.current = null;
      if (data.status === "completed") { toast.success("Site generated!"); load(); }
      if (data.status === "failed") toast.error("Generation failed on: " + data.failed_step);
    }
  }, [id, load]);

  useEffect(() => { load().catch(() => toast.error("Failed to load site")); fetchStatus(); return () => { clearInterval(pollRef.current); clearInterval(timerRef.current); }; }, [id]); // eslint-disable-line

  const startPolling = (startedAt) => {
    const t0 = startedAt ? new Date(startedAt).getTime() : Date.now();
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed((Date.now() - t0) / 1000), 100);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(fetchStatus, 700);
  };

  const generate = async (retry = false) => {
    try {
      const { data } = await api.post(`/sites/${id}/${retry ? "retry" : "generate"}`);
      startPolling(Date.now());
      setJob({ status: "running", steps: [], started_at: new Date().toISOString() });
      fetchStatus();
    } catch (e) { toast.error(apiErr(e)); }
  };

  // resume timer/polling if job already running on load
  useEffect(() => {
    if (job?.status === "running" && !pollRef.current) startPolling(job.started_at);
  }, [job?.status]); // eslint-disable-line

  const [restoring, setRestoring] = useState(null);
  const restoreVersion = async (v) => {
    setRestoring(v.id);
    try {
      const { data } = await api.post(`/sites/${id}/versions/${v.id}/activate`);
      toast.success(`Switched to v${v.version_num}${data.redeployed ? " & redeployed live" : ""}`);
      setViewing(null);
      await load();
      if (iframeRef.current) iframeRef.current.src = `${API}/preview/${id}/?t=${Date.now()}`;
    } catch (e) { toast.error(apiErr(e)); } finally { setRestoring(null); }
  };

  const genLogo = async () => {
    setLogoBusy(true);
    try {
      const data = await runJob(`/sites/${id}/generate-logo`);
      setLogo(data);
      toast.success("Logo generated — review & apply");
    } catch (e) { toast.error(apiErr(e)); } finally { setLogoBusy(false); }
  };

  const useLogo = async () => {
    try {
      await api.post(`/sites/${id}/apply-logo`, { logo_url: logo.logo_url, palette: logo.palette });
      toast.success("Logo & colors applied to this site");
      setLogo(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const deploy = async () => {
    setDeploying(true);
    try {
      await api.post(`/sites/${id}/deploy`);
      toast.success("Deployed to Cloudflare Pages");
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setDeploying(false); }
  };

  const checkDomain = useCallback(async () => {
    setDomainBusy(true);
    try {
      const { data } = await api.get(`/sites/${id}/domain-status`);
      setDomainStatus(data.domains || {});
    } catch (e) { /* ignore transient */ } finally { setDomainBusy(false); }
  }, [id]);

  useEffect(() => { if (site?.custom_domains?.length) checkDomain(); }, [site?.custom_domains?.length, checkDomain]);

  const connectDomain = async () => {
    setConnecting(true);
    try {
      await api.post(`/sites/${id}/connect-domain`, { domain: domainInput.trim(), include_www: includeWww });
      toast.success("Domain connected — SSL is provisioning");
      setShowDomainInput(false); setDomainInput("");
      await load(); checkDomain();
    } catch (e) { toast.error(apiErr(e)); } finally { setConnecting(false); }
  };

  const onGscFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!/^google[a-zA-Z0-9]+\.html$/.test(f.name)) { setGscMsg("File must be named like google<code>.html"); e.target.value = ""; return; }
    setGscBusy(true); setGscMsg("");
    try {
      const content = await f.text();
      const { data } = await api.post(`/sites/${id}/gsc-upload`, { filename: f.name, content });
      setGscMsg(data.redeployed ? "Uploaded & redeployed ✓ — click Verify in GSC" : "Uploaded ✓ — deploy the site, then Verify in GSC");
      await load();
    } catch (err) { setGscMsg(apiErr(err)); } finally { setGscBusy(false); e.target.value = ""; }
  };

  const onRefFile = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    const big = files.find((f) => f.size > 10 * 1024 * 1024);
    if (big) return toast.error(`${big.name} is larger than 10 MB`);
    const read = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res({ name: f.name, data_base64: r.result }); r.onerror = rej; r.readAsDataURL(f); });
    try {
      const added = await Promise.all(files.map(read));
      setEditRef((prev) => [...(Array.isArray(prev) ? prev : []), ...added].slice(0, 6));
    } catch { toast.error("Could not read that file"); }
  };
  const proposeEdit = async () => {
    if (!editInstruction.trim()) return;
    setEditBusy(true);
    try {
      const { data } = await api.post(`/sites/${id}/edit-instruction`, { instruction: editInstruction.trim(), attachments: Array.isArray(editRef) ? editRef : [] });
      setPendingEdit(data);
      if (data.preview_url) setViewing({ label: "Proposed changes — not published yet", url: data.preview_url });
      toast.success("Proposed changes ready — inspect the full preview below");
    } catch (e) { toast.error(apiErr(e)); } finally { setEditBusy(false); }
  };
  const approveEdit = async () => {
    setApproving(true);
    try {
      const { data } = await api.post(`/sites/${id}/edit-approve/${pendingEdit.edit_id}`);
      toast.success(data.redeployed ? "Applied & redeployed live" : "Applied — new version saved");
      setPendingEdit(null); setEditInstruction(""); setEditRef([]); setViewing(null);
      await load(); if (iframeRef.current) iframeRef.current.src = `${API}/preview/${id}/?t=${Date.now()}`;
    } catch (e) { toast.error(apiErr(e)); } finally { setApproving(false); }
  };
  const discardEdit = async () => {
    if (pendingEdit) { try { await api.post(`/sites/${id}/edit-reject/${pendingEdit.edit_id}`); } catch (e) { /* ignore */ } }
    setPendingEdit(null); setViewing(null);
  };

  if (!site) return <div className="text-slate-500">Loading…</div>;

  const running = job?.status === "running";
  const failed = job?.status === "failed";
  const previewUrl = `${API}/preview/${site.id}/`;
  const shownUrl = viewing?.url || previewUrl;
  const curIdx = versions.findIndex((v) => v.id === site.current_version_id);
  const olderV = curIdx >= 0 ? versions[curIdx + 1] : null;
  const newerV = curIdx > 0 ? versions[curIdx - 1] : null;
  const siteRoot = site.custom_domain ? `https://${site.custom_domain}/` : (site.deployed_url || "");
  const siteRootSlash = siteRoot ? siteRoot.replace(/\/?$/, "/") : "";
  const sitemapUrl = siteRootSlash ? `${siteRootSlash}sitemap.xml` : "";
  const gscSitemapLink = siteRootSlash ? `https://search.google.com/search-console/sitemaps?resource_id=${encodeURIComponent(siteRootSlash)}` : "";
  const hasPreview = site.status === "preview" || site.status === "live";
  const totalSecs = job?.finished_at && job?.started_at
    ? (new Date(job.finished_at) - new Date(job.started_at)) / 1000 : elapsed;

  return (
    <div className="animate-fade-up">
      <Link to="/websites" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200 mb-4"><ArrowLeft className="w-4 h-4" /> My Websites</Link>

      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className={ui.pageHeader}>{site.name}</h1>
            <span className={`${ui.badge} ${statusStyle[site.status]}`}>{site.status}</span>
          </div>
          <p className={`${ui.muted} mt-1`}>{site.category_name} · {site.business?.city}, {site.business?.state} · {site.business?.phone}</p>
        </div>
        <div className="flex gap-2">
          {!running && hasPreview && (
            <button data-testid="deploy-btn" onClick={deploy} disabled={deploying} className={ui.btnGhost}>
              {deploying ? <Loader2 className="w-4 h-4 spin" /> : <Rocket className="w-4 h-4 text-emerald-400" />}
              {site.deployed_url ? "Redeploy" : "Deploy Live"}
            </button>
          )}
          {!running && (
            <button data-testid="generate-btn" onClick={() => generate(false)} className={ui.btnPrimary}>
              <Play className="w-4 h-4" /> {hasPreview ? "Regenerate" : "Generate"}
            </button>
          )}
        </div>
      </div>

      {site.deployed_url && (
        <div className="mb-6 flex items-center gap-3 bg-emerald-950/20 border border-emerald-500/30 rounded-xl px-4 py-3" data-testid="live-banner">
          <Globe className="w-5 h-5 text-emerald-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-emerald-500/80 font-mono">Live on Cloudflare Pages{deploying ? " · deploying…" : ""}</div>
            <a href={site.deployed_url} target="_blank" rel="noreferrer" data-testid="live-url" className="text-sm text-emerald-200 hover:text-emerald-100 font-mono truncate block">{site.deployed_url}</a>
          </div>
          <button onClick={() => { navigator.clipboard.writeText(site.deployed_url); toast.success("Copied"); }} className="p-1.5 text-emerald-400 hover:text-emerald-200"><Copy className="w-4 h-4" /></button>
          <a href={site.deployed_url} target="_blank" rel="noreferrer" data-testid="open-live" className={ui.btnGhost}><ExternalLink className="w-4 h-4" /> Open</a>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Brand & Logo */}
          <div className={`${ui.card} p-6`} data-testid="logo-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className={ui.sectionHeader}>Brand & Logo</h2>
              <button data-testid="generate-logo-btn" onClick={genLogo} disabled={logoBusy} className={ui.btnGhost}>
                {logoBusy ? <Loader2 className="w-4 h-4 spin" /> : <Wand2 className="w-4 h-4 text-indigo-400" />}
                {logo || site.business?.logo_url ? "Regenerate Logo" : "Generate Logo"}
              </button>
            </div>
            {(logo || site.business?.logo_url) ? (
              <div>
                <div className="rounded-lg bg-white border border-slate-800 p-6 flex items-center justify-center min-h-[120px]">
                  <img data-testid="logo-preview" src={logo?.logo_url || site.business.logo_url} alt="Logo preview" style={{ maxHeight: 96, maxWidth: "100%" }} />
                </div>
                <button data-testid="download-logo-btn" onClick={async () => {
                  try {
                    const url = logo?.logo_url || site.business.logo_url;
                    const res = await fetch(url); const blob = await res.blob();
                    const a = document.createElement("a");
                    a.href = URL.createObjectURL(blob);
                    a.download = `${(site.name || "logo").replace(/\s+/g, "-").toLowerCase()}-logo.png`;
                    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
                  } catch { toast.error("Could not download logo"); }
                }} className={`${ui.btnGhost} mt-4`}><Download className="w-4 h-4" /> Download logo</button>
                {logo ? (
                  <>
                    <div className="flex gap-2 mt-4 items-center">
                      <span className="text-xs text-slate-500 font-mono uppercase tracking-wider">Derived palette</span>
                      {logo.palette.map((c, i) => <span key={i} className="w-7 h-7 rounded-md border border-slate-700" style={{ background: c.hex }} title={`${c.name} · ${c.role}`} />)}
                    </div>
                    <div className="flex gap-2 mt-4">
                      <button data-testid="use-logo-btn" onClick={useLogo} className={ui.btnPrimary}><Check className="w-4 h-4" /> Use this logo & colors</button>
                      <button onClick={genLogo} disabled={logoBusy} className={ui.btnGhost}>{logoBusy ? <Loader2 className="w-4 h-4 spin" /> : <RefreshCw className="w-4 h-4" />} Regenerate</button>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-emerald-400 mt-3">Logo applied — it will appear on the generated site and theme its colors.</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Generate a unique 2-color transparent logo with OpenAI. The whole site's colors are derived from it. You'll preview it before applying. (~30–60s)</p>
            )}
          </div>

          {hasPreview && (
            <div className={`${ui.card} p-6`} data-testid="edit-panel">
              <h2 className={`${ui.sectionHeader} mb-1 flex items-center gap-2`}><MessageSquare className="w-4 h-4 text-indigo-400" /> Edit by Instruction</h2>
              <p className="text-xs text-slate-500 mb-4">Describe a change in plain English — text, company name, phone, address, service areas, services or logo. Attach files for reference (or an image to use as the new logo). Preview, then approve to publish{site.deployed_url ? " & redeploy live" : ""}.</p>
              <textarea data-testid="edit-instruction-input" value={editInstruction} onChange={(e) => setEditInstruction(e.target.value)} placeholder={'e.g. "Make the hero headline punchier and mention 24/7 emergency service"'} className={ui.textarea} rows={3} />
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer hover:text-slate-200">
                  <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf,.docx,.xlsx,.csv,.txt,.md" onChange={onRefFile} className="hidden" data-testid="edit-ref-file" />
                  <Paperclip className="w-3.5 h-3.5" /><span className="underline">Attach files (JPG, PNG, PDF, Word, Excel, CSV, TXT)</span>
                </label>
              </div>
              {Array.isArray(editRef) && editRef.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="edit-attachments">
                  {editRef.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-slate-800 text-[11px] text-slate-200" data-testid={`edit-attachment-${i}`}>
                      {f.name}
                      <button onClick={() => setEditRef(editRef.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-400" data-testid={`edit-attachment-remove-${i}`}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              <button data-testid="propose-edit-btn" onClick={proposeEdit} disabled={editBusy || !editInstruction.trim()} className={`${ui.btnPrimary} mt-3`}>{editBusy ? <Loader2 className="w-4 h-4 spin" /> : <Send className="w-4 h-4" />} Propose changes</button>

              {pendingEdit && (
                <div className="mt-5 border-t border-slate-800 pt-4" data-testid="edit-preview">
                  <p className="text-sm text-slate-200 mb-3"><span className="text-indigo-400 font-semibold">Proposed:</span> {pendingEdit.summary}</p>
                  <div className="space-y-3 max-h-80 overflow-auto pr-1">
                    {pendingEdit.changes.map((c, i) => (
                      <div key={i} className="text-xs" data-testid={`edit-change-${i}`}>
                        <div className="font-mono uppercase tracking-wider text-slate-500 mb-1">{c.field}</div>
                        {c.field === "logo" ? (
                          <div className="flex items-center gap-3">
                            {c.before ? <img src={c.before} alt="old logo" className="h-14 bg-white rounded p-1 opacity-60" /> : <span className="text-slate-500">no logo</span>}
                            <span className="text-slate-500">→</span>
                            <img src={c.after} alt="new logo" className="h-14 bg-white rounded p-1" data-testid="edit-new-logo" />
                          </div>
                        ) : (<>
                          <div className="rounded bg-rose-950/20 border border-rose-500/20 text-rose-200/80 p-2 mb-1 line-through decoration-rose-500/40">{c.before}</div>
                          <div className="rounded bg-emerald-950/20 border border-emerald-500/20 text-emerald-200 p-2">{c.after}</div>
                        </>)}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button data-testid="view-edit-preview-btn" onClick={() => { setViewing({ label: "Proposed changes — not published yet", url: pendingEdit.preview_url }); document.querySelector('[data-testid="preview-shell"]')?.scrollIntoView({ behavior: "smooth" }); }} className={`${ui.btnGhost} mt-4 mr-2`}><Eye className="w-4 h-4" /> Preview full site</button>
                  <a data-testid="open-edit-preview-btn" href={pendingEdit.preview_url} target="_blank" rel="noreferrer" className={`${ui.btnGhost} mt-4`}><ExternalLink className="w-4 h-4" /> Open in new tab</a>
                  </div>
                  <div className="flex gap-2 mt-3">
                  <button data-testid="approve-edit-btn" onClick={approveEdit} disabled={approving} className={ui.btnPrimary}>{approving ? <Loader2 className="w-4 h-4 spin" /> : <Check className="w-4 h-4" />} Approve &amp; publish</button>
                    <button data-testid="discard-edit-btn" onClick={discardEdit} disabled={approving} className={ui.btnGhost}>Discard</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pipeline */}
          {(running || failed || job?.status === "completed") && (
            <div className={`${ui.card} p-6`} data-testid="pipeline-panel">
              <div className="flex items-center justify-between mb-5">
                <h2 className={ui.sectionHeader}>Generation Pipeline</h2>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-mono text-sm font-semibold" data-testid="pipeline-timer">
                  <Timer className="w-4 h-4" /> {fmtTime(totalSecs || 0)}
                </span>
              </div>
              <div className="space-y-2.5">
                {(job.steps || []).map((s) => <StepRow key={s.key} step={s} />)}
              </div>
              {failed && (
                <div className="mt-4 flex items-center justify-between bg-rose-950/30 border border-rose-500/30 rounded-lg p-3">
                  <span className="text-sm text-rose-300">{job.error}</span>
                  <button data-testid="retry-btn" onClick={() => generate(true)} className={ui.btnGhost}><RefreshCw className="w-4 h-4" /> Retry step</button>
                </div>
              )}
              {job.checks?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Automated Checks</div>
                  <div className="grid sm:grid-cols-2 gap-1.5" data-testid="checks-list">
                    {job.checks.map((c) => (
                      <div key={c.name} className="flex items-center gap-2 text-xs">
                        {c.passed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-rose-400 shrink-0" />}
                        <span className={c.passed ? "text-slate-400" : "text-rose-300"}>{c.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Preview */}
          {hasPreview && (
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-2xl flex flex-col" data-testid="preview-shell">
              <div className="bg-slate-950 border-b border-slate-800 px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0 bg-slate-900 border border-slate-800 rounded-md px-3 py-1.5 text-xs font-mono text-slate-400 truncate flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${viewing ? "bg-amber-400" : "bg-emerald-500"}`} /> {shownUrl}
                </div>
                <button onClick={() => { navigator.clipboard.writeText(shownUrl); toast.success("Copied"); }} className="p-1.5 text-slate-400 hover:text-slate-200"><Copy className="w-4 h-4" /></button>
                <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800">
                  <button onClick={() => setDevice("desktop")} className={`p-1.5 rounded ${device === "desktop" ? "bg-slate-700 text-indigo-400" : "text-slate-500"}`}><Monitor className="w-4 h-4" /></button>
                  <button onClick={() => setDevice("mobile")} className={`p-1.5 rounded ${device === "mobile" ? "bg-slate-700 text-indigo-400" : "text-slate-500"}`}><Smartphone className="w-4 h-4" /></button>
                </div>
                <a href={shownUrl} target="_blank" rel="noreferrer" data-testid="open-preview" className={ui.btnGhost}><ExternalLink className="w-4 h-4" /> Open</a>
              </div>
              {viewing && (
                <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 flex items-center justify-between text-xs" data-testid="preview-viewing-banner">
                  <span className="text-amber-300 font-medium">Viewing: {viewing.label}</span>
                  <button onClick={() => setViewing(null)} data-testid="preview-back-to-current" className="text-slate-300 hover:text-white underline">Back to current version</button>
                </div>
              )}
              <div className="bg-slate-800 p-4 flex justify-center h-[640px] overflow-auto">
                <iframe ref={iframeRef} title="preview" data-testid="preview-iframe" src={shownUrl}
                  className="bg-white rounded-md shadow-xl border border-slate-700 transition-all"
                  style={{ width: device === "mobile" ? 390 : "100%", height: device === "mobile" ? 844 : "100%", maxWidth: "100%" }} />
              </div>
            </div>
          )}

          {!hasPreview && !running && !job && (
            <div className={`${ui.card} p-10 text-center`}>
              <p className="text-slate-400 mb-4">This site is a draft. Generate it to produce a live preview in under 5 minutes.</p>
              <button data-testid="generate-btn-empty" onClick={() => generate(false)} className={`${ui.btnPrimary} mx-auto`}><Play className="w-4 h-4" /> Generate</button>
            </div>
          )}
        </div>

        {/* Sidebar: info + versions */}
        <div className="space-y-6">
          <div className={`${ui.card} p-6`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={ui.sectionHeader}>Details</h2>
              {!editingDetails && (
                <button data-testid="edit-details-btn" onClick={startEdit} className={ui.btnGhost}><Pencil className="w-4 h-4 text-indigo-400" /> Edit</button>
              )}
            </div>
            {editingDetails ? (
              <div className="space-y-3" data-testid="edit-details-form">
                <div><label className="text-xs text-slate-500">Business name</label><input data-testid="edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={ui.input} /></div>
                <div><label className="text-xs text-slate-500">Phone</label><input data-testid="edit-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={ui.input} /></div>
                <div><label className="text-xs text-slate-500">Address</label><input data-testid="edit-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={ui.input} /></div>
                <div className="grid grid-cols-3 gap-2">
                  <div><label className="text-xs text-slate-500">City</label><input data-testid="edit-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={ui.input} /></div>
                  <div><label className="text-xs text-slate-500">State</label><input data-testid="edit-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={ui.input} /></div>
                  <div><label className="text-xs text-slate-500">ZIP</label><input data-testid="edit-zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className={ui.input} /></div>
                </div>
                <div><label className="text-xs text-slate-500">Hours</label><input data-testid="edit-hours" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="e.g. Mon–Fri 8am–6pm" disabled={form.hours_247} className={ui.input} /></div>
                <label className="flex items-center gap-2 text-xs text-slate-400"><input type="checkbox" data-testid="edit-247" checked={form.hours_247} onChange={(e) => setForm({ ...form, hours_247: e.target.checked })} /> Open 24 hours a day, 7 days a week</label>
                <div className="flex gap-2 pt-1">
                  <button data-testid="save-details-btn" onClick={saveDetails} disabled={savingDetails} className={ui.btnPrimary}>{savingDetails ? <Loader2 className="w-4 h-4 spin" /> : <Check className="w-4 h-4" />} Save</button>
                  <button onClick={() => setEditingDetails(false)} className="text-xs text-slate-400 hover:text-slate-200">Cancel</button>
                </div>
              </div>
            ) : (
              <dl className="space-y-2.5 text-sm">
                {[["Business", site.name], ["Phone", site.business?.phone], ["Address", `${site.business?.address}`], ["Location", `${site.business?.city}, ${site.business?.state} ${site.business?.zip}`],
                  ["Hours", site.business?.hours_247 ? "24 hours a day, 7 days a week" : (site.business?.hours || "—")], ["Services", `${site.selected_services?.length} selected`],
                  ["Service area", `${site.service_area?.length} areas`], ["Coordinates", site.geo?.pin ? `${site.geo.pin.lat.toFixed(4)}, ${site.geo.pin.lng.toFixed(4)}` : "City-centered"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4"><dt className="text-slate-500">{k}</dt><dd className="text-slate-200 text-right">{v}</dd></div>
                ))}
              </dl>
            )}
          </div>

          {site.deployed_url && (
            <div className={`${ui.card} p-6`} data-testid="domain-panel">
              <h2 className={`${ui.sectionHeader} mb-4 flex items-center gap-2`}><Globe className="w-4 h-4 text-emerald-400" /> Custom Domain</h2>
              {(site.custom_domains?.length && !showDomainInput) ? (
                <div className="space-y-3">
                  {site.custom_domains.map((d) => {
                    const st = domainStatus[d]?.status || site.domain_statuses?.[d] || "pending";
                    const active = st === "active";
                    return (
                      <div key={d} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-slate-800 bg-slate-950/40" data-testid={`domain-row-${d}`}>
                        <a href={`https://${d}`} target="_blank" rel="noreferrer" className="text-sm font-mono text-slate-200 hover:text-emerald-300 truncate">{d}</a>
                        <span className={`${ui.badge} ${active ? "bg-emerald-950/60 border-emerald-500/30 text-emerald-300" : "bg-indigo-950/60 border-indigo-500/40 text-indigo-300"}`}>{active ? "active" : st}</span>
                      </div>
                    );
                  })}
                  <div className="flex gap-2 items-center">
                    <button data-testid="refresh-domain-btn" onClick={checkDomain} disabled={domainBusy} className={ui.btnGhost}>{domainBusy ? <Loader2 className="w-4 h-4 spin" /> : <RefreshCw className="w-4 h-4" />} Check status</button>
                    <button onClick={() => setShowDomainInput(true)} className="text-xs text-slate-400 hover:text-slate-200">Change</button>
                  </div>
                  <p className="text-[11px] text-slate-500">DNS &amp; SSL are handled by Cloudflare — the certificate can take a few minutes to go active after connecting.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <input data-testid="domain-input" value={domainInput} onChange={(e) => setDomainInput(e.target.value)} placeholder="example.com" className={ui.input} />
                  <label className="flex items-center gap-2 text-xs text-slate-400"><input type="checkbox" checked={includeWww} onChange={(e) => setIncludeWww(e.target.checked)} data-testid="domain-www-check" /> Also connect www.{domainInput || "example.com"}</label>
                  <div className="flex gap-2 items-center">
                    <button data-testid="connect-domain-btn" onClick={connectDomain} disabled={connecting || !domainInput.trim()} className={ui.btnPrimary}>{connecting ? <Loader2 className="w-4 h-4 spin" /> : <Globe className="w-4 h-4" />} Connect Domain</button>
                    {site.custom_domains?.length ? <button onClick={() => setShowDomainInput(false)} className="text-xs text-slate-400 hover:text-slate-200">Cancel</button> : null}
                  </div>
                  <p className="text-[11px] text-slate-500">The domain must already exist in your Cloudflare account (nameservers pointed to Cloudflare).</p>
                </div>
              )}
              <div className="pt-4 mt-4 border-t border-slate-800 space-y-2" data-testid="gsc-panel">
                <p className="text-xs font-semibold text-slate-300">Google Search Console</p>
                <p className="text-[11px] text-slate-500">In GSC, add a <b>URL-prefix</b> property, pick the <b>HTML file</b> method, download the <code>google….html</code> file and upload it here. We host it at your site root and redeploy so Google can verify.</p>
                {site.gsc_file?.name && <p className="text-[11px] text-emerald-400" data-testid="gsc-current">Hosted: /{site.gsc_file.name}</p>}
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="file" accept=".html,text/html" onChange={onGscFile} className="hidden" data-testid="gsc-file" />
                  <span className={ui.btnGhost}>{gscBusy ? <Loader2 className="w-4 h-4 spin" /> : <Globe className="w-4 h-4" />} Upload verification file</span>
                </label>
                {gscMsg && <span className="text-[11px] text-slate-400 block" data-testid="gsc-msg">{gscMsg}</span>}
                {siteRoot && (
                  <div className="pt-3 mt-2 border-t border-slate-800/60 space-y-1.5" data-testid="sitemap-panel">
                    <p className="text-xs font-semibold text-slate-300">Sitemap</p>
                    <div className="flex items-center gap-2">
                      <code className="text-[11px] text-slate-400 truncate flex-1" data-testid="sitemap-url">{sitemapUrl}</code>
                      <button onClick={() => { navigator.clipboard.writeText(sitemapUrl); toast.success("Sitemap URL copied"); }} className="p-1 text-slate-400 hover:text-slate-200"><Copy className="w-3.5 h-3.5" /></button>
                    </div>
                    <a data-testid="submit-sitemap-btn" href={gscSitemapLink} target="_blank" rel="noreferrer" className={ui.btnGhost}><ExternalLink className="w-4 h-4 text-emerald-400" /> Submit sitemap in GSC</a>
                    <p className="text-[11px] text-slate-500">Opens Search Console's Sitemaps page for this property — type <code>sitemap.xml</code> and click Submit.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={`${ui.card} p-6`}>
            <h2 className={`${ui.sectionHeader} mb-3 flex items-center gap-2`}><History className="w-4 h-4 text-indigo-400" /> Version History</h2>
            <p className="text-[11px] text-slate-500 mb-3">Every version is kept. Preview any version, then switch to it — you can always go back to a newer one.</p>
            <div className="flex gap-2 mb-3">
              <button data-testid="version-older-btn" disabled={!olderV || !!restoring} onClick={() => restoreVersion(olderV)} className={`${ui.btnGhost} flex-1 justify-center disabled:opacity-40`}><Undo2 className="w-4 h-4" /> Older</button>
              <button data-testid="version-newer-btn" disabled={!newerV || !!restoring} onClick={() => restoreVersion(newerV)} className={`${ui.btnGhost} flex-1 justify-center disabled:opacity-40`}>Newer <Redo2 className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2" data-testid="versions-list">
              {versions.map((v) => (
                <div key={v.id} className={`p-3 rounded-lg border ${v.id === site.current_version_id ? "border-indigo-500/40 bg-indigo-950/10" : "border-slate-800 bg-slate-950/40"}`} data-testid={`version-${v.version_num}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-mono text-slate-200">v{v.version_num}{v.id === site.current_version_id && <span className="ml-2 text-[10px] text-indigo-400">CURRENT</span>}</span>
                    <div className="flex items-center gap-3">
                      <button data-testid={`version-preview-${v.version_num}`} onClick={() => setViewing({ label: `Version v${v.version_num}${v.id === site.current_version_id ? " (current)" : ""}`, url: `${API}/preview/${site.id}/v/${v.id}/` })} className="text-xs text-slate-400 hover:text-indigo-400 inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Preview</button>
                      {v.id !== site.current_version_id && (
                        <button data-testid={`rollback-${v.version_num}`} disabled={!!restoring} onClick={() => restoreVersion(v)} className="text-xs text-slate-400 hover:text-indigo-400 inline-flex items-center gap-1">{restoring === v.id ? <Loader2 className="w-3.5 h-3.5 spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Restore</button>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{v.change_summary} · {new Date(v.created_at).toLocaleString()}</div>
                  <div className="text-[11px] text-slate-600 mt-0.5">{v.created_by}</div>
                </div>
              ))}
              {versions.length === 0 && <p className="text-sm text-slate-600">No versions yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
